import React, { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import OnDemand from "../cloneApps/onDemand/OnDemand";
import Home from "../Home/Home";

import ScrollToTop from "./ScrollToTop";
import Layout from "../Layouts/Layout";

const serverRequire = typeof window === "undefined" ? eval("require") : null;

const RouteFallback = () => (
  <div className="loader-only-css">
    <div className="loader-inner-css">
      <div></div>
    </div>
  </div>
);

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error) {
    console.error("Route render failed:", error);
  }

  render() {
    if (this.state.hasError) {
      return <RouteFallback />;
    }

    return this.props.children;
  }
}

const isChunkLoadError = (error) => {
  const message = `${error?.name || ""} ${error?.message || ""}`;
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i.test(message);
};

const browserLazy = (loader) =>
  lazy(() =>
    loader()
      .then((module) => {
        sessionStorage.removeItem("route-chunk-reload");
        return module;
      })
      .catch((error) => {
        if (isChunkLoadError(error) && sessionStorage.getItem("route-chunk-reload") !== "1") {
          sessionStorage.setItem("route-chunk-reload", "1");
          window.location.reload();
          return new Promise(() => {});
        }

        throw error;
      })
  );

const ssrLazy = (loader, modulePath) => {
  if (typeof window !== "undefined") {
    return browserLazy(loader);
  }

  const loadedModule = serverRequire(modulePath);
  return loadedModule.default || loadedModule;
};

const RouteBoundaryScope = ({ children }) => {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      {children}
    </RouteErrorBoundary>
  );
};

// User Layout Components
const AboutNew = ssrLazy(() => import("../aboutNew/AboutNew"), "../aboutNew/AboutNew");
const Contact = ssrLazy(() => import("../Contact/Contact"), "../Contact/Contact");
const ThankYou = ssrLazy(() => import("../thankyou/ThankYou"), "../thankyou/ThankYou");
const PrivacyPolicy = ssrLazy(() => import("../privacy/PrivacyPolicy"), "../privacy/PrivacyPolicy");
const PageNotFound = ssrLazy(() => import("../404page/PageNotFound"), "../404page/PageNotFound");
const Pricing = ssrLazy(() => import("../pricing/Pricing"), "../pricing/Pricing");
const MediaPage = ssrLazy(() => import("../Media_Page/MediaPage"), "../Media_Page/MediaPage");


// Clone Apps
const CloneAppsDynamic = ssrLazy(() => import("../cloneApps/UberClone/CloneAppsDynamic"), "../cloneApps/UberClone/CloneAppsDynamic");
const LyftClone = ssrLazy(() => import("../cloneApps/LyftClone/LyftClone"), "../cloneApps/LyftClone/LyftClone");
const ZipcarClone = ssrLazy(() => import("../cloneApps/ZipcarClone/ZipcarClone"), "../cloneApps/ZipcarClone/ZipcarClone");
const CabifyClone = ssrLazy(() => import("../cloneApps/cabifyClone/CabifyClone"), "../cloneApps/cabifyClone/CabifyClone");
const BoltClone = ssrLazy(() => import("../cloneApps/boltClone/BoltClone"), "../cloneApps/boltClone/BoltClone");
const XanhClone = ssrLazy(() => import("../cloneApps/xanhClone/XanhClone"), "../cloneApps/xanhClone/XanhClone");
const MaiLinhClone = ssrLazy(() => import("../cloneApps/maiLinhClone/MaiLinhClone"), "../cloneApps/maiLinhClone/MaiLinhClone");
const ZoomZoom = ssrLazy(() => import("../cloneApps/zoomZoom/ZoomZoom"), "../cloneApps/zoomZoom/ZoomZoom");
const YellowCab = ssrLazy(() => import("../cloneApps/yellowCab/YellowCab"), "../cloneApps/yellowCab/YellowCab");
const URideClone = ssrLazy(() => import("../cloneApps/uRideClone/URideClone"), "../cloneApps/uRideClone/URideClone");
const Taxis99 = ssrLazy(() => import("../cloneApps/99taxis/Taxis99"), "../cloneApps/99taxis/Taxis99");
const UberClone = ssrLazy(() => import("../cloneApps/UberClone/UberClone"), "../cloneApps/UberClone/UberClone");
const EasyTaxi = ssrLazy(() => import("../cloneApps/easyTaxi/EasyTaxi"), "../cloneApps/easyTaxi/EasyTaxi");
const VadeTaxi = ssrLazy(() => import("../cloneApps/vadeTaxi/VadeTaxi"), "../cloneApps/vadeTaxi/VadeTaxi");
const FreeNow = ssrLazy(() => import("../cloneApps/freeNow/FreeNow"), "../cloneApps/freeNow/FreeNow");
const BlaBlaClone = ssrLazy(() => import("../cloneApps/blabla/BlaBlaClone"), "../cloneApps/blabla/BlaBlaClone");
const BlaBlaItaly = ssrLazy(() => import("../cloneApps/blablaItaly/BlaBlaItaly"), "../cloneApps/blablaItaly/BlaBlaItaly");
const GrabClone = ssrLazy(() => import("../cloneApps/grabClone/GrabClone"), "../cloneApps/grabClone/GrabClone");
const PoparideClone = ssrLazy(() => import("../cloneApps/poparide/PoparideClone"), "../cloneApps/poparide/PoparideClone");
const GroceryApp = ssrLazy(() => import("../cloneApps/groceryApp/GroceryApp"), "../cloneApps/groceryApp/GroceryApp");
const BlinkitClone = ssrLazy(() => import("../cloneApps/blinkit/BlinkitClone"), "../cloneApps/blinkit/BlinkitClone");
const Bigbasket = ssrLazy(() => import("../cloneApps/bigbasket/Bigbasket"), "../cloneApps/bigbasket/Bigbasket");
const FreshDirect = ssrLazy(() => import("../cloneApps/freshDirect/FreshDirect"), "../cloneApps/freshDirect/FreshDirect");
const Swiggy = ssrLazy(() => import("../cloneApps/swiggy/Swiggy"), "../cloneApps/swiggy/Swiggy");
const CareemClone = ssrLazy(() => import("../cloneApps/careem/CareemClone"), "../cloneApps/careem/CareemClone");
const UberEats = ssrLazy(() => import("../cloneApps/uberEats/UberEats"), "../cloneApps/uberEats/UberEats");
const DoordashClone = ssrLazy(() => import("../cloneApps/doordash/DoordashClone"), "../cloneApps/doordash/DoordashClone");
const DeliverooClone = ssrLazy(() => import("../cloneApps/deliveroo/DeliverooClone"), "../cloneApps/deliveroo/DeliverooClone");
const OlaClone = ssrLazy(() => import("../cloneApps/olaClone/OlaClone"), "../cloneApps/olaClone/OlaClone");
const BlueSmart = ssrLazy(() => import("../cloneApps/blueSmart/BlueSmart"), "../cloneApps/blueSmart/BlueSmart");


// Industries
const Restaurant = ssrLazy(() => import("../restaurant/Restaurant"), "../restaurant/Restaurant");
const Telemedicine = ssrLazy(() => import("../telemedicine/Telemedicine"), "../telemedicine/Telemedicine");
const Education = ssrLazy(() => import("../education/Education"), "../education/Education");
const FoodAndGrocery = ssrLazy(() => import("../foodAndGrocery/FoodAndGrocery"), "../foodAndGrocery/FoodAndGrocery");
const HealthCare = ssrLazy(() => import("../healthcare/HealthCare"), "../healthcare/HealthCare");
const Industries = ssrLazy(() => import("../industries/Industries"), "../industries/Industries");
const Pharmacy = ssrLazy(() => import("../pharmacy/Pharmacy"), "../pharmacy/Pharmacy");
const Ecommerce = ssrLazy(() => import("../ecommerce/Ecommerce"), "../ecommerce/Ecommerce");


// Solutions
const ShuttleSoftware = ssrLazy(() => import("../shuttle_software/ShuttleSoftware"), "../shuttle_software/ShuttleSoftware");
const CarRental = ssrLazy(() => import("../carRental/CarRental"), "../carRental/CarRental");
const CarPooling = ssrLazy(() => import("../carPooling/CarPooling"), "../carPooling/CarPooling");
const WhiteLabelSolutions = ssrLazy(() => import("../WhiteLabelSolutions/WhiteLabel"), "../WhiteLabelSolutions/WhiteLabel");
const BikeRental = ssrLazy(() => import("../bikeRental/BikeRental"), "../bikeRental/BikeRental");
const TaxiDispatchSoftware = ssrLazy(() => import("../taxiDispatchSoftware/taxiDispatchSoftware"), "../taxiDispatchSoftware/taxiDispatchSoftware");
const CustomAppDevelopment = ssrLazy(() => import("../customAppDevelopment/CustomAppDevelopment"), "../customAppDevelopment/CustomAppDevelopment");
const WebAppDevelopment = ssrLazy(() => import("../webAppDevelopment/WebAppDevelopment"), "../webAppDevelopment/WebAppDevelopment");
const TaxiSoftware = ssrLazy(() => import("../TaxiSoftware/TaxiSoftware"), "../TaxiSoftware/TaxiSoftware");


// blogs front end
const BlogListing = ssrLazy(() => import("../Blogs/BlogListing"), "../Blogs/BlogListing");
const BlogDetails = ssrLazy(() => import("../BlogDetails/BlogDetails"), "../BlogDetails/BlogDetails");
const AIBlogDetail = ssrLazy(() => import("../AIblogDetail/AIBlogDetail"), "../AIblogDetail/AIBlogDetail");

// Case Study front end
const CaseNewList = ssrLazy(() => import("../caseNew/list/CaseNewList"), "../caseNew/list/CaseNewList");
const CaseNewDetails = ssrLazy(() => import("../caseNew/details/CaseNewDetails"), "../caseNew/details/CaseNewDetails");
// const CaseStudyListing = lazy(() => import("../caseStudy/CaseStudyListing"));
// const CaseStudyDetailNew = lazy(() => import("../caseStudyDetails/CaseStudyDetailNew"));

// features front end
const Features = ssrLazy(() => import("../Features/Features"), "../Features/Features");
const FeatureNew = ssrLazy(() => import("../featureNew/FeatureNew"), "../featureNew/FeatureNew");

const BusManagement = ssrLazy(() => import("../busManagement/BusManagement"), "../busManagement/BusManagement");
const Limo = ssrLazy(() => import("../limo/Limo"), "../limo/Limo");

// ─── Admin Panel (lazy loaded — no SSR/SEO needed) ────────────────────────────
const Login = lazy(() => import("../login/Login"));
const AdminLayout = lazy(() => import("../Layouts/AdminLayout"));
const Dashboard = lazy(() => import("../adminPanel/dashboard/Dashboard"));
const BlogListingAdmin = lazy(() => import("../adminPanel/blogs/BlogListingAdmin"));
const AddBlogAdmin = lazy(() => import("../adminPanel/blogs/AddBlogAdmin"));
const EditBlogAdmin = lazy(() => import("../adminPanel/blogs/EditBlogAdmin"));
const AIBlogs = lazy(() => import("../adminPanel/aiBlogs/AIBlogs"));
const AddAIBlogs = lazy(() => import("../adminPanel/aiBlogs/AddAIBlogs"));
const EditAIBlogs = lazy(() => import("../adminPanel/aiBlogs/EditAIBlogs"));
const AuthorListing = lazy(() => import("../adminPanel/author/AuthorListing"));
const AddAuthor = lazy(() => import("../adminPanel/author/AddAuthor"));
const EditAuthor = lazy(() => import("../adminPanel/author/EditAuthor"));
const Tags = lazy(() => import("../adminPanel/tags/Tags"));
const AddTags = lazy(() => import("../adminPanel/tags/AddTags"));
const EditTags = lazy(() => import("../adminPanel/tags/EditTags"));
const Category = lazy(() => import("../adminPanel/category/Category"));
const AddCategory = lazy(() => import("../adminPanel/category/AddCategory"));
const EditCategory = lazy(() => import("../adminPanel/category/EditCategory"));
const SeoListing = lazy(() => import("../adminPanel/seo/SeoListing"));
const SeoCreate = lazy(() => import("../adminPanel/seo/SeoCreate"));
const SeoEdit = lazy(() => import("../adminPanel/seo/SeoEdit"));
const SchemaManagement = lazy(() => import("../adminPanel/schema/SchemaManagement"));
const SitemapManagement = lazy(() => import("../adminPanel/sitemap/SitemapManagement"));
const LlmsManagement = lazy(() => import("../adminPanel/llms/LlmsManagement"));
const RobotsManagement = lazy(() => import("../adminPanel/robots/RobotsManagement"));
const FaqListing = lazy(() => import("../adminPanel/faq/FaqListing"));
const AddFaq = lazy(() => import("../adminPanel/faq/AddFaq"));
const EditFaq = lazy(() => import("../adminPanel/faq/EditFaq"));
const CaseStudyListAdmin = lazy(() => import("../adminPanel/caseStudy/CaseStudyListAdmin"));
const AddCaseStudy = lazy(() => import("../adminPanel/caseStudy/AddCaseStudy"));
const EditCaseStudy = lazy(() => import("../adminPanel/caseStudy/EditCaseStudy"));
const CaseCategoryList = lazy(() => import("../adminPanel/caseCategory/CaseCategoryList"));
const AddCaseCategory = lazy(() => import("../adminPanel/caseCategory/AddCaseCategory"));
const EditCaseCategory = lazy(() => import("../adminPanel/caseCategory/EditCaseCategory"));
const NewsLetter = lazy(() => import("../adminPanel/newsLetter/NewsLetter"));
const ContactListingAdmin = lazy(() => import("../adminPanel/contact/contactListingAdmin"));
const FeaturePageListing = lazy(() => import("../adminPanel/webPages/FeaturePageListing"));
const FeaturePageCreate = lazy(() => import("../adminPanel/webPages/FeaturePageCreate"));
const FeaturePageEdit = lazy(() => import("../adminPanel/webPages/FeaturePageEdit"));
const FeatureEdit = lazy(() => import("../adminPanel/feature/FeatureEdit"));
const FeatureCreate = lazy(() => import("../adminPanel/feature/FeatureCreate"));
const FeatureListing = lazy(() => import("../adminPanel/feature/FeatureListing"));
const AdminMedia = lazy(() => import("../adminPanel/media/AdminMedia"));
const AddMedia = lazy(() => import("../adminPanel/media/AddMedia"));
const EditMedia = lazy(() => import("../adminPanel/media/EditMedia"));
const CloneApps = lazy(() => import("../adminPanel/cloneApps/CloneApps"));
const AddCloneApps = lazy(() => import("../adminPanel/cloneApps/AddCloneApps"));
const EditCloneApps = lazy(() => import("../adminPanel/cloneApps/EditCloneApps"));
const AddTestimonial = lazy(() => import("../adminPanel/testimonial/AddTestimonial"));
const TestimonialList = lazy(() => import("../adminPanel/testimonial/TestimonialList"));
const EditTestimonial = lazy(() => import("../adminPanel/testimonial/EditTestimonial"));
const EmailDynamic = lazy(() => import("../adminPanel/emailDynamic/EmailDynamic"));
const AddEmails = lazy(() => import("../adminPanel/emailDynamic/AddEmails"));
const EditEmails = lazy(() => import("../adminPanel/emailDynamic/EditEmails"));
const ClientLogos = lazy(() => import("../adminPanel/clientLogos/ClientLogos"));
const AddClientLogo = lazy(() => import("../adminPanel/clientLogos/AddClientLogo"));
const EditClientLogo = lazy(() => import("../adminPanel/clientLogos/EditClientLogo"));
const AdminPrice = lazy(() => import("../adminPanel/pricing/AdminPrice"));
const AddPrice = lazy(() => import("../adminPanel/pricing/AddPrice"));
const EditPrice = lazy(() => import("../adminPanel/pricing/EditPrice"));
const Users = lazy(() => import("../adminPanel/rbac/Users"));
const Roles = lazy(() => import("../adminPanel/rbac/Roles"));
const Permissions = lazy(() => import("../adminPanel/rbac/Permissions"));

// SSR-safe redirect — returns null on server, <Navigate> on client
const ClientRedirect = ({ to }) => {
  if (typeof window === 'undefined') return null;
  return <Navigate to={to} replace />;
};

export const NavRouter = ({ initialData, initialFaq }) => {
  return (
    <>


      <ScrollToTop />
      <RouteBoundaryScope>
      <Suspense fallback={<RouteFallback />}>
      <Routes>

        {/*=============== Admin Layout Start ===============*/}
        <Route path="Login" element={<Suspense fallback={<RouteFallback />}><Login /></Suspense>} />
        <Route path="/admin" element={<Suspense fallback={<RouteFallback />}><AdminLayout /></Suspense>}>
          <Route index element={<Suspense fallback={<RouteFallback />}><Dashboard /></Suspense>} />

          <Route path="blog" element={<Suspense fallback={<RouteFallback />}><BlogListingAdmin /></Suspense>} />
          <Route path="add-blog" element={<Suspense fallback={<RouteFallback />}><AddBlogAdmin /></Suspense>} />
          <Route path="edit-blog/:blog_id" element={<Suspense fallback={<RouteFallback />}><EditBlogAdmin /></Suspense>} />

          <Route path="ai-blog" element={<Suspense fallback={<RouteFallback />}><AIBlogs /></Suspense>} />
          <Route path="add-ai-blog" element={<Suspense fallback={<RouteFallback />}><AddAIBlogs /></Suspense>} />
          <Route path="edit-ai-blog/:aiblog_id" element={<Suspense fallback={<RouteFallback />}><EditAIBlogs /></Suspense>} />

          <Route path="author" element={<Suspense fallback={<RouteFallback />}><AuthorListing /></Suspense>} />
          <Route path="add-author" element={<Suspense fallback={<RouteFallback />}><AddAuthor /></Suspense>} />
          <Route path="edit-author/:author_id" element={<Suspense fallback={<RouteFallback />}><EditAuthor /></Suspense>} />

          <Route path="client-logos" element={<Suspense fallback={<RouteFallback />}><ClientLogos /></Suspense>} />
          <Route path="add-client-logo" element={<Suspense fallback={<RouteFallback />}><AddClientLogo /></Suspense>} />
          <Route path="edit-client-logo/:clientlogo_id" element={<Suspense fallback={<RouteFallback />}><EditClientLogo /></Suspense>} />

          <Route path="tags" element={<Suspense fallback={<RouteFallback />}><Tags /></Suspense>} />
          <Route path="add-tags" element={<Suspense fallback={<RouteFallback />}><AddTags /></Suspense>} />
          <Route path="edit-tags/:tags_id" element={<Suspense fallback={<RouteFallback />}><EditTags /></Suspense>} />

          <Route path="category" element={<Suspense fallback={<RouteFallback />}><Category /></Suspense>} />
          <Route path="add-category" element={<Suspense fallback={<RouteFallback />}><AddCategory /></Suspense>} />
          <Route path="edit-category/:category_id" element={<Suspense fallback={<RouteFallback />}><EditCategory /></Suspense>} />

          <Route path="seo" element={<Suspense fallback={<RouteFallback />}><SeoListing /></Suspense>} />
          <Route path="add-seo" element={<Suspense fallback={<RouteFallback />}><SeoCreate /></Suspense>} />
          <Route path="edit-seo/:seo_id" element={<Suspense fallback={<RouteFallback />}><SeoEdit /></Suspense>} />
          <Route path="global-seo" element={<Suspense fallback={<RouteFallback />}><SchemaManagement /></Suspense>} />
          <Route path="schema" element={<ClientRedirect to="/admin/global-seo" />} />
          <Route path="sitemap" element={<Suspense fallback={<RouteFallback />}><SitemapManagement sitemapKey="primary" /></Suspense>} />
          <Route path="sitemap-blogs" element={<Suspense fallback={<RouteFallback />}><SitemapManagement sitemapKey="blogs" /></Suspense>} />
          <Route path="sitemap-ai-blogs" element={<Suspense fallback={<RouteFallback />}><SitemapManagement sitemapKey="ai-blogs" /></Suspense>} />
          <Route path="sitemap-services" element={<Suspense fallback={<RouteFallback />}><SitemapManagement sitemapKey="services" /></Suspense>} />
          <Route path="llms" element={<Suspense fallback={<RouteFallback />}><LlmsManagement /></Suspense>} />
          <Route path="robots" element={<Suspense fallback={<RouteFallback />}><RobotsManagement /></Suspense>} />
          <Route path="faq" element={<Suspense fallback={<RouteFallback />}><FaqListing /></Suspense>} />
          <Route path="add-faq" element={<Suspense fallback={<RouteFallback />}><AddFaq /></Suspense>} />
          <Route path="edit-faq/:faq_id" element={<Suspense fallback={<RouteFallback />}><EditFaq /></Suspense>} />

          <Route path="feature-pages" element={<Suspense fallback={<RouteFallback />}><FeaturePageListing /></Suspense>} />
          <Route path="add-feature-page" element={<Suspense fallback={<RouteFallback />}><FeaturePageCreate /></Suspense>} />
          <Route path="edit-feature-page/:featurepage_id" element={<Suspense fallback={<RouteFallback />}><FeaturePageEdit /></Suspense>} />

          <Route path="features" element={<Suspense fallback={<RouteFallback />}><FeatureListing /></Suspense>} />
          <Route path="add-feature" element={<Suspense fallback={<RouteFallback />}><FeatureCreate /></Suspense>} />
          <Route path="edit-feature/:featurepage_id" element={<Suspense fallback={<RouteFallback />}><FeatureEdit /></Suspense>} />

          <Route path="price" element={<Suspense fallback={<RouteFallback />}><AdminPrice /></Suspense>} />
          <Route path="add-price" element={<Suspense fallback={<RouteFallback />}><AddPrice /></Suspense>} />
          <Route path="edit-price/:price_id" element={<Suspense fallback={<RouteFallback />}><EditPrice /></Suspense>} />

          <Route path="case-study" element={<Suspense fallback={<RouteFallback />}><CaseStudyListAdmin /></Suspense>} />
          <Route path="add-case-study" element={<Suspense fallback={<RouteFallback />}><AddCaseStudy /></Suspense>} />
          <Route path="edit-case-study/:casestudy_id" element={<Suspense fallback={<RouteFallback />}><EditCaseStudy /></Suspense>} />

          <Route path="media" element={<Suspense fallback={<RouteFallback />}><AdminMedia /></Suspense>} />
          <Route path="add-media" element={<Suspense fallback={<RouteFallback />}><AddMedia /></Suspense>} />
          <Route path="edit-media/:media_id" element={<Suspense fallback={<RouteFallback />}><EditMedia /></Suspense>} />

          <Route path="case-category" element={<Suspense fallback={<RouteFallback />}><CaseCategoryList /></Suspense>} />
          <Route path="add-case-category" element={<Suspense fallback={<RouteFallback />}><AddCaseCategory /></Suspense>} />
          <Route path="edit-case-category/:casecategory_id" element={<Suspense fallback={<RouteFallback />}><EditCaseCategory /></Suspense>} />

          <Route path="clone-app" element={<Suspense fallback={<RouteFallback />}><CloneApps /></Suspense>} />
          <Route path="add-clone-app" element={<Suspense fallback={<RouteFallback />}><AddCloneApps /></Suspense>} />
          <Route path="edit-clone-app/:cloneapp_url" element={<Suspense fallback={<RouteFallback />}><EditCloneApps /></Suspense>} />

          <Route path="testimonial" element={<Suspense fallback={<RouteFallback />}><TestimonialList /></Suspense>} />
          <Route path="add-testimonial" element={<Suspense fallback={<RouteFallback />}><AddTestimonial /></Suspense>} />
          <Route path="edit-testimonial/:testimonial_id" element={<Suspense fallback={<RouteFallback />}><EditTestimonial /></Suspense>} />

          <Route path="emails" element={<Suspense fallback={<RouteFallback />}><EmailDynamic /></Suspense>} />
          <Route path="add-emails" element={<Suspense fallback={<RouteFallback />}><AddEmails /></Suspense>} />
          <Route path="edit-emails/:email_id" element={<Suspense fallback={<RouteFallback />}><EditEmails /></Suspense>} />

          <Route path="contact" element={<Suspense fallback={<RouteFallback />}><ContactListingAdmin /></Suspense>} />
          <Route path="newsletter" element={<Suspense fallback={<RouteFallback />}><NewsLetter /></Suspense>} />

          <Route path="users" element={<Suspense fallback={<RouteFallback />}><Users /></Suspense>} />
          <Route path="roles" element={<Suspense fallback={<RouteFallback />}><Roles /></Suspense>} />
          <Route path="permissions" element={<Suspense fallback={<RouteFallback />}><Permissions /></Suspense>} />
        </Route>
        {/*=============== Admin Layout Finish ===============*/}


        {/*================ User Layout Routes ================*/}
        <Route path="/" element={<Layout initialFaq={initialFaq} />}>
          <Route index element={<Home initialData={initialData} />} />

          <Route path="about" element={<AboutNew initialData={initialData}/>} />
          {/* <Route path="about" element={<About />} /> */}
          <Route path="contact" element={<Contact />} />
          <Route path="thankyou" element={<ThankYou />} />
          <Route path="404" element={<PageNotFound />} />
          <Route path="*" element={<ClientRedirect to={"/404"} />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="pricing" element={<Pricing initialData={initialData}/>} />
          {/* <Route path="pricingnew" element={<PriceNew />} /> */}
          <Route path="media" element={<MediaPage initialData={initialData}/>} />
          {/* <Route path="coming-soon" element={<ComingSoon />} /> */}


          {/* Blog Front End Routes */}
          <Route path="blog" element={<BlogListing initialData={initialData}/>} />
          <Route path="blog/:blog_slug" element={<BlogDetails initialData={initialData} initialFaq={initialFaq}/>} />
          <Route path="ai-blog/:aiblog_slug" element={<AIBlogDetail initialData={initialData}/>} />


          {/* Case Study front end Routes */}
          <Route path="case-study" element={<CaseNewList initialData={initialData}/>} />
          <Route path="case-study/:casestudy_slug" element={<CaseNewDetails initialData={initialData}/>} />
          {/* <Route path="case-study" element={<CaseStudyListing />} /> */}
          {/* <Route path="case-study/:casestudy_slug" element={<CaseStudyDetailNew />} /> */}
          {/* <Route path="case-study" element={<CaseStudy />} /> */}
          {/* <Route path="case-study-detail" element={<CaseStudyDetail />} /> */}


          {/* Feature front end Routes */}
          <Route path="features/:featurepageId" element={<Features initialData={initialData}/>} />
          <Route path="features/taxi-software" element={<FeatureNew initialData={initialData}/>} />
          {/* <Route path="features" element={<Features />} /> */}


          {/* All Solution Routes */}
          <Route path="white-lable-carpool-app-dispatch-software" element={<CarPooling initialData={initialData}/>} />
          <Route path="best-taxi-dispatch-software" element={<TaxiSoftware initialData={initialData}/>} />
          <Route path="shuttle-software" element={<ShuttleSoftware initialData={initialData}/>} />
          <Route path="car-rental-software" element={<CarRental initialData={initialData}/>} />
          <Route path="white-label-solutions" element={<WhiteLabelSolutions initialData={initialData}/>} />
          <Route path="bike-rental" element={<BikeRental initialData={initialData}/>} />
          <Route path="airport-transfer-software" element={<TaxiDispatchSoftware initialData={initialData}/>} />
          <Route path="custom-app-development" element={<CustomAppDevelopment initialData={initialData}/>} />
          <Route path="web-app-development" element={<WebAppDevelopment initialData={initialData}/>} />
          <Route path="on-demand-delivery-app" element={<OnDemand initialData={initialData}/>} />
          <Route path="bus-management-software" element={<BusManagement initialData={initialData}/>} />
          <Route path="best-limo-booking-software" element={<Limo initialData={initialData}/>} />


          {/*All Industries Routes */}
          <Route path="best-logistic-app-development-company" element={<Industries initialData={initialData}/>} />
          <Route path="custom-healthcare-app-development-services" element={<HealthCare initialData={initialData}/>} />
          <Route path="food-grocery-delivery-app-development-company" element={<FoodAndGrocery initialData={initialData}/>} />
          <Route path="best-pharmacy-app-development-company" element={<Pharmacy initialData={initialData}/>} />
          <Route path="ecommerce-mobile-app-development-company" element={<Ecommerce initialData={initialData}/>} />
          <Route path="education-app-development-services" element={<Education initialData={initialData}/>} />
          <Route path="telemedicine-app-development-company" element={<Telemedicine initialData={initialData}/>} />
          <Route path="restaurant-app-development-company" element={<Restaurant initialData={initialData}/>} />


          {/* All Clone App Routes*/}
          <Route path="clone-app/:cloneapp_url" element={<CloneAppsDynamic initialData={initialData}/>} />
          <Route path="clone-app/lyft-clone-app" element={<LyftClone initialData={initialData}/>} />
          <Route path="clone-app/zipcar-clone-app" element={<ZipcarClone initialData={initialData}/>} />
          <Route path="clone-app/cabify-clone-app" element={<CabifyClone initialData={initialData}/>} />
          <Route path="clone-app/bolt-clone-app" element={<BoltClone initialData={initialData}/>} />
          <Route path="clone-app/mai-linh-clone-app" element={<MaiLinhClone initialData={initialData}/>} />
          <Route path="clone-app/xanh-sm-clone-app" element={<XanhClone initialData={initialData}/>} />
          <Route path="clone-app/zoom-zoom-clone-app" element={<ZoomZoom initialData={initialData}/>} />
          <Route path="clone-app/yellow-cab-clone-app" element={<YellowCab initialData={initialData}/>} />
          <Route path="clone-app/uride-clone-app" element={<URideClone initialData={initialData}/>} />
          <Route path="clone-app/easy-taxi-clone-app" element={<EasyTaxi initialData={initialData}/>} />
          <Route path="clone-app/va-de-taxi-clone-app" element={<VadeTaxi initialData={initialData}/>} />
          <Route path="clone-app/free-now-clone-app" element={<FreeNow initialData={initialData}/>} />
          <Route path="clone-app/99taxis-clone-app" element={<Taxis99 initialData={initialData}/>} />
          <Route path="clone-app/uber-clone-app" element={<UberClone initialData={initialData}/>} />
          <Route path="clone-app/blabla-car-clone-app" element={<BlaBlaClone initialData={initialData}/>} />
          <Route path="clone-app/blabla-car-clone-app-italy" element={<BlaBlaItaly initialData={initialData}/>} />
          <Route path="clone-app/grab-clone-app" element={<GrabClone initialData={initialData}/>} />
          <Route path="clone-app/poparide-clone-app" element={<PoparideClone initialData={initialData}/>} />
          <Route path="clone-app/grocery-clone-app" element={<GroceryApp initialData={initialData}/>} />
          <Route path="clone-app/blinkit-clone-app" element={<BlinkitClone initialData={initialData}/>} />
          <Route path="clone-app/bigbasket-clone-app" element={<Bigbasket initialData={initialData}/>} />
          <Route path="clone-app/freshdirect-clone-app" element={<FreshDirect initialData={initialData}/>} />
          <Route path="clone-app/swiggy-clone-app" element={<Swiggy initialData={initialData}/>} />
          <Route path="clone-app/careem-clone-app" element={<CareemClone initialData={initialData}/>} />
          <Route path="clone-app/uber-eats-clone-app" element={<UberEats initialData={initialData}/>} />
          <Route path="clone-app/doordash-clone-app" element={<DoordashClone initialData={initialData}/>} />
          <Route path="clone-app/deliveroo-clone-app" element={<DeliverooClone initialData={initialData}/>} />
          <Route path="clone-app/ola-clone-app" element={<OlaClone initialData={initialData}/>} />
          <Route path="clone-app/blue-smart-clone-app" element={<BlueSmart initialData={initialData}/>} />
          

          {/* Redirections */}
          <Route path="/car-pooling" element={<ClientRedirect to="/white-lable-carpool-app-dispatch-software" replace />} />
          <Route path="/taxi-software" element={<ClientRedirect to="/best-taxi-dispatch-software" replace />} />
          <Route path="/car-rental" element={<ClientRedirect to="/car-rental-software" replace />} />
          <Route path="/industries" element={<ClientRedirect to={"/best-logistic-app-development-company"} replace />} />
          <Route path="healthcare" element={<ClientRedirect to={"/custom-healthcare-app-development-services"} replace />} />
          <Route path="food-and-grocery-app" element={<ClientRedirect to={"/food-grocery-delivery-app-development-company"} replace />} />
          <Route path="pharmacy-app" element={<ClientRedirect to={"/best-pharmacy-app-development-company"} replace />} />
          <Route path="ecommerce-app" element={<ClientRedirect to={"/ecommerce-mobile-app-development-company"} replace />} />
          <Route path="lyft-clone-app" element={<ClientRedirect to={"/clone-app/lyft-clone-app"} replace />} />
          <Route path="uber-clone-app" element={<ClientRedirect to={"/clone-app/uber-clone-app"} replace />} />
          <Route path="/best-taxi-dispatc" element={<ClientRedirect to="/best-taxi-dispatch-software" replace />} />
          <Route path="/healthcare" element={<ClientRedirect to="/custom-healthcare-app-development-services" replace />} />
          <Route path="/ecommerce-app" element={<ClientRedirect to="/ecommerce-mobile-app-development-company" replace />} />
          <Route path="/blog-detail/undefined" element={<ClientRedirect to="/blog" replace />} />
          <Route path="/casestudy/ontap-case-study" element={<ClientRedirect to="/case-study/ontap-case-study" replace />} />
          <Route path="/casestudy/togopool-case-study" element={<ClientRedirect to="/case-study/togopool-case-study" replace />} />
          <Route path="/best-taxi-dispatch-software/best-taxi-dispatch-software" element={<ClientRedirect to="/best-taxi-dispatch-software" replace />} />
          <Route path="/blog/how-to-choose-the-right-taxi-dispatch-software-for-your-growing-busin" element={<ClientRedirect to="/blog/how-to-choose-the-right-taxi-dispatch-software-for-your-growing-business" replace />} />
          <Route path="/blog/key-considerations-for-choosing-taxi-management-software-for-g" element={<ClientRedirect to="/blog/key-considerations-for-choosing-taxi-management-software-for-growing-fleets" replace />} />
          <Route path="/blog/what-is-taxi-dispatch-software-and-how-it-can-transform-yo" element={<ClientRedirect to="/blog/what-is-taxi-dispatch-software-and-how-it-can-transform-your-taxi-business" replace />} />
          <Route path="blog/a-comprehensive-guide-to-taxi-dispatch-system-price" element={<ClientRedirect to="/blog/a-comprehensive-guide-to-taxi-dispatch-system-prices-and-roi" replace />} />
          <Route path="/blog/a-comprehensive-guide-to-custom-app-vs-white-label-for-ta" element={<ClientRedirect to="/blog/a-comprehensive-guide-to-custom-app-vs-white-label-for-taxi-companies" replace />} />
          <Route path="/features/66f7a4b7770fe7ade1f72f93" element={<ClientRedirect to="/features/taxi-dispatch-software" replace />} />
          <Route path="/features/66f7a494770fe7ade1f72f84" element={<ClientRedirect to="/features/taxi-dispatch-software" replace />} />
          <Route path="/features/66f7a486770fe7ade1f72f80" element={<ClientRedirect to="/features/taxi-dispatch-software" replace />} />
          <Route path="/features/66f7a4ab770fe7ade1f72f8e" element={<ClientRedirect to="/features/taxi-dispatch-software" replace />} />
          <Route path="/features/66f7a49f770fe7ade1f72f89" element={<ClientRedirect to="/features/taxi-dispatch-software" replace />} />
          <Route path="/features/66f295c630826ed7475eee6a" element={<ClientRedirect to="/features/taxi-dispatch-software" replace />} />
          <Route path="/features/66f3ba421e2135e008de309d" element={<ClientRedirect to="/features/taxi-dispatch-software" replace />} />
          <Route path="/blog/create-lyft-and-uber-alternative-app-to-start-your-own-taxibusiness" element={<ClientRedirect to="/blog/create-lyft-and-uber-alternative-app-to-start-your-own-taxi-business" replace />} />
          <Route path="/carpool-software" element={<ClientRedirect to="/white-lable-carpool-app-dispatch-software" replace />} />
          <Route path="/blog/the-rise-of-white-label-taxi-apps:-what-you-need-to-know" element={<ClientRedirect to="/blog/the-rise-of-white-label-taxi-apps-what-you-need-to-know" replace />} />
          <Route path="/bike-renta" element={<ClientRedirect to="/bike-rental" replace />} />
          <Route path="/blog/top-10-taxi-dispatch-software-providers" element={<ClientRedirect to="/blog/top-taxi-dispatch-software-provider-features-pricing-and-reviews" replace />} />
          <Route path="/taxi-software/features" element={<ClientRedirect to="/features/taxi-software" replace />} />
          <Route path="/careers" element={<ClientRedirect to="/" replace />} />
          <Route path="/custom-app-development(Custom" element={<ClientRedirect to="/custom-app-development" replace />} />
          <Route path="/food-grocery-delivery-app-d" element={<ClientRedirect to="/food-grocery-delivery-app-development-company" replace />} />
          <Route path="/blog/guide-on-taxi-app-deve" element={<ClientRedirect to="/blog/guide-on-taxi-app-development-all-that-you-ought-to-be-aware-in-2025" replace />} />
          <Route path="blog/why-do-modern-passenger-transport-companies-c" element={<ClientRedirect to="/blog/why-do-modern-passenger-transport-companies-choose-white-label-taxi-app-solutions" replace />} />
          <Route path="/news" element={<ClientRedirect to="/media" replace />} />
          <Route path="/blog/the-role-of-taxi-scheduling-software-in-modern-transportati" element={<ClientRedirect to="/blog/the-role-of-taxi-scheduling-software-in-modern-transportation" replace />} />
          <Route path="/telemedicine-app-developmen" element={<ClientRedirect to="/blog/telemedicine-app-development-key-factors-to-consider-for-your-healthcare-venture" replace />} />
          <Route path='/blog/telemedicine-app-development-key-factors-to-consider-for-your-healthcare-venture”' element={<ClientRedirect to="/blog/telemedicine-app-development-key-factors-to-consider-for-your-healthcare-venture" replace />} />
          <Route path="/blog/2025-and-beyond-how-taxi-dispatch-software-is-driving-innovation-in-the-uk-and-germanys-transport-industry" element={<ClientRedirect to="/blog/how-taxi-software-enhances-local-transportation-services-in-the-uk-and-germany" replace />} />
          <Route path="/blog/why-carpooling-open-source-software-is-growing-in-the-uk" element={<ClientRedirect to="/blog/why-taxi-software-dispatch-adoption-is-growing-fast-in-uk-germany" replace />} />
          <Route path="/blog/why-taxi-businesses-in-taiwan-are-switching-to-white-label-software)" element={<ClientRedirect to="/blog/why-taxi-businesses-in-taiwan-are-switching-to-white-label-software" replace />} />
          <Route path="/blog/how-to-choose-the-right-taxi-dispatch-software-for" element={<ClientRedirect to="/blog/how-to-choose-the-right-taxi-dispatch-software-for-your-growing-business" replace />} />
          <Route path="/blog/maximizing-t" element={<ClientRedirect to="/blog/maximizing-the-potential-of-your-taxi-business-with-a-lyft-clone-app" replace />} />
          <Route path="/blog/the-benefits-taxi-businesses-gain-from-a-taxi-serv" element={<ClientRedirect to="/blog/benefits-of-taxi-cab-dispatch-software-system-for-taxi-companies" replace />} />
          <Route path="/case-study-listing" element={<ClientRedirect to="/case-study" replace />} />
          <Route path="/casestudy" element={<ClientRedirect to="/case-study" replace />} />
          <Route path="/custom-app-development(Customized" element={<ClientRedirect to="/custom-app-development" replace />} />

        </Route>
      </Routes>
      </Suspense>
      </RouteBoundaryScope>
    </>
  );
};
